// --- App: layout + session lifecycle (state lives in jotai atoms, src/state/) ---

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { CircleAlert, TriangleAlert } from "lucide";
import { lazy, Suspense, useCallback, useEffect, useMemo } from "react";
import { CategoryFilter } from "./components/CategoryFilter";
import { DebugPanel } from "./components/DebugPanel";
import { EnrichmentPanel } from "./components/EnrichmentPanel";
import { ExportPanel } from "./components/ExportPanel";
import { GpxUpload } from "./components/GpxUpload";
import { HeaderStrip } from "./components/HeaderStrip";
import { Icon } from "./components/Icon";
import { PoiDetail } from "./components/PoiDetail";
import { PoiList } from "./components/PoiList";
import { RouteMap } from "./components/RouteMap";
import { Settings } from "./components/Settings";
import { type PillTab, Sheet, TabBar, TabPanel } from "./components/Sheet";
import { TraceSummary } from "./components/TraceSummary";
import { t } from "./lib/i18n";
import { clearSession, hasSession, loadSession, saveSession } from "./lib/session";
import {
  filteredPoisAtom,
  isProcessingAtom,
  progressAtom,
  progressRatioAtom,
  retryQueryAtom,
  routeErrorAtom,
  routeWarningAtom,
  stageAtom,
  tracesAtom,
} from "./state/route";
import { resetAllAtom, restoreSessionAtom, sessionSnapshotAtom } from "./state/session";
import {
  paletteAtom,
  selectedPoiIdAtom,
  sheetDetentAtom,
  showResumePromptAtom,
  targetLanguageAtom,
  themeAtom,
} from "./state/ui";

const EnrichmentSandbox = lazy(() =>
  import("./components/EnrichmentSandbox").then((module) => ({
    default: module.EnrichmentSandbox,
  })),
);

// Static build (GitHub Pages): no server, so no enrichment; tab 03 says so instead of vanishing
const STANDALONE = import.meta.env.VITE_STANDALONE === "true";
const TABS: PillTab[] = ["trace", "poi", "enrich", "export"];

export default function App() {
  const sandboxMode = useMemo(
    () =>
      typeof window !== "undefined" && new URLSearchParams(window.location.search).has("sandbox"),
    [],
  );

  const stage = useAtomValue(stageAtom);
  const traces = useAtomValue(tracesAtom);
  const filteredPois = useAtomValue(filteredPoisAtom);
  const isProcessing = useAtomValue(isProcessingAtom);
  const progress = useAtomValue(progressAtom);
  const progressRatio = useAtomValue(progressRatioAtom);
  const warning = useAtomValue(routeWarningAtom);
  const error = useAtomValue(routeErrorAtom);

  const targetLanguage = useAtomValue(targetLanguageAtom);
  const theme = useAtomValue(themeAtom);
  const palette = useAtomValue(paletteAtom);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.palette = palette;
    document.documentElement.lang = targetLanguage;
  }, [theme, palette, targetLanguage]);
  const [selectedPoiId, setSelectedPoiId] = useAtom(selectedPoiIdAtom);
  const [showResumePrompt, setShowResumePrompt] = useAtom(showResumePromptAtom);
  const detent = useAtomValue(sheetDetentAtom);

  const retryQuery = useSetAtom(retryQueryAtom);
  const handleReset = useSetAtom(resetAllAtom);
  const restoreSession = useSetAtom(restoreSessionAtom);
  const snapshot = useAtomValue(sessionSnapshotAtom);

  // On mount: offer to resume a saved session
  useEffect(() => {
    if (hasSession()) setShowResumePrompt(true);
  }, [setShowResumePrompt]);

  const handleResume = useCallback(() => {
    const session = loadSession();
    if (session) restoreSession(session);
    setShowResumePrompt(false);
  }, [restoreSession, setShowResumePrompt]);

  const handleDismissResume = useCallback(() => {
    clearSession();
    setShowResumePrompt(false);
  }, [setShowResumePrompt]);

  // Clear selection when the selected POI leaves the filtered set
  useEffect(() => {
    if (selectedPoiId && !filteredPois.some((p) => p.id === selectedPoiId)) {
      setSelectedPoiId(null);
    }
  }, [filteredPois, selectedPoiId, setSelectedPoiId]);

  // Auto-save once the pipeline is done with POIs
  useEffect(() => {
    if (snapshot && snapshot.pois.length > 0) saveSession(snapshot);
  }, [snapshot]);

  // Full-bleed map; the sheet (mobile) or panel (desktop) holds one tab at a time
  return (
    <div className="shell" data-detent={detent}>
      <main className="map-area">
        <RouteMap />
      </main>
      <Settings />
      <Sheet strip={<HeaderStrip />}>
        {/* 01: Load a route: upload, resume, pipeline status, errors */}
        <TabPanel tab="trace">
          {showResumePrompt && (
            <div className="card session-prompt">
              <p>{t("session.prompt", targetLanguage)}</p>
              <div className="box-actions">
                <button type="button" className="btn btn-primary" onClick={handleResume}>
                  {t("session.resume", targetLanguage)}
                </button>
                <button type="button" className="btn btn-quiet" onClick={handleDismissResume}>
                  {t("session.fresh", targetLanguage)}
                </button>
              </div>
            </div>
          )}

          {(stage === "idle" || (stage === "error" && traces.length === 0)) &&
            !showResumePrompt && <GpxUpload />}

          {progress && (
            <div className="status" role="status" aria-live="polite">
              {isProcessing && <span className="spinner" />}
              <div className="status-body">
                <span>{progress}</span>
                {progressRatio != null && (
                  <div
                    className="progress"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(progressRatio * 100)}
                  >
                    <div style={{ width: `${Math.round(progressRatio * 100)}%` }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {warning && (
            <div className="notice notice-warning">
              <Icon node={TriangleAlert} />
              <div className="notice-body">
                <p>
                  <strong>{t("status.warning", targetLanguage)}</strong> {warning}
                </p>
                <div className="box-actions">
                  <button type="button" className="btn btn-sm" onClick={retryQuery}>
                    {t("action.retryQuery", targetLanguage)}
                  </button>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="notice notice-danger" role="alert">
              <Icon node={CircleAlert} />
              <div className="notice-body">
                <p>
                  <strong>{t("status.error", targetLanguage)}</strong> {error}
                </p>
                <div className="box-actions">
                  {traces.length > 0 && (
                    <button type="button" className="btn btn-sm" onClick={retryQuery}>
                      {t("action.retryQuery", targetLanguage)}
                    </button>
                  )}
                  <button type="button" className="btn btn-quiet btn-sm" onClick={handleReset}>
                    {traces.length > 0
                      ? t("action.startOver", targetLanguage)
                      : t("action.tryAgain", targetLanguage)}
                  </button>
                </div>
              </div>
            </div>
          )}

          {stage === "done" && (
            <>
              <TraceSummary />
              <div className="box-actions">
                <GpxUpload append />
                <button type="button" className="btn btn-quiet" onClick={handleReset}>
                  {t("action.startOver", targetLanguage)}
                </button>
              </div>
            </>
          )}
        </TabPanel>

        {/* 02: the POIs along the route and their filters; a selected POI's detail replaces them,
            which stay mounted underneath (sort, scroll, open sections survive) */}
        <TabPanel tab="poi">
          <PoiDetail />
          <div className="poi-browse" hidden={selectedPoiId !== null}>
            <CategoryFilter />
            {stage === "done" && <PoiList />}
          </div>
        </TabPanel>

        {/* 03: Enrich (optional, needs the server: the static build explains why it is not there) */}
        <TabPanel tab="enrich">
          {STANDALONE ? (
            <p className="card p-4">{t("enrich.static", targetLanguage)}</p>
          ) : (
            <>
              <EnrichmentPanel />
              {sandboxMode && (
                <Suspense fallback={null}>
                  <EnrichmentSandbox />
                </Suspense>
              )}
            </>
          )}
        </TabPanel>

        {/* 04: Export offline */}
        <TabPanel tab="export">
          <ExportPanel />
        </TabPanel>

        {/* Debug log, opened from the settings */}
        <TabPanel tab="debug">
          <DebugPanel />
        </TabPanel>
      </Sheet>
      <TabBar tabs={TABS} />
    </div>
  );
}
